import React from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSwipe } from '../hooks/useSwipe';

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  title: string;
  currentIndex: number;
  totalImages: number;
  onNext?: () => void;
  onPrev?: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  title,
  currentIndex,
  totalImages,
  onNext,
  onPrev
}) => {
  console.log('🔍 ImageModal - Props recebidas:', { isOpen, imageUrl, title, currentIndex, totalImages });
  console.log('🔍 ImageModal - Component renderizando, isOpen:', isOpen);
  
  // Setup swipe handlers
  const swipeRef = useSwipe({
    onSwipeLeft: () => onNext && onNext(),
    onSwipeRight: () => onPrev && onPrev()
  });
  
  if (!isOpen) {
    console.log('🔍 ImageModal - Modal fechado, removendo classe antes de retornar null');
    document.body.classList.remove('modal-open');
    return null;
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft' && onPrev) {
      onPrev();
    } else if (e.key === 'ArrowRight' && onNext) {
      onNext();
    }
  };

  React.useEffect(() => {
    console.log('🔍 ImageModal useEffect - isOpen:', isOpen);
    console.log('🔍 ImageModal useEffect - body classList antes:', document.body.classList.toString());
    console.log('🔍 ImageModal useEffect - body style.overflow antes:', document.body.style.overflow);
    
    if (isOpen) {
      document.body.classList.add('modal-open');
      console.log('🔍 ImageModal - Adicionando classe modal-open');
    } else {
      document.body.classList.remove('modal-open');
      console.log('🔍 ImageModal - Removendo classe modal-open');
    }
    
    console.log('🔍 ImageModal useEffect - body classList depois:', document.body.classList.toString());
    console.log('🔍 ImageModal useEffect - body style.overflow depois:', document.body.style.overflow);
    console.log('🔍 ImageModal useEffect - computed overflow:', window.getComputedStyle(document.body).overflow);
    
    return () => {
      console.log('🔍 ImageModal cleanup - removendo modal-open');
      document.body.classList.remove('modal-open');
      console.log('🔍 ImageModal cleanup - body classList final:', document.body.classList.toString());
    };
  }, [isOpen]);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
      >
        <X className="w-8 h-8" />
      </button>

      {/* Navigation buttons - hidden on mobile, swipe replaces them */}
      {totalImages > 1 && onPrev && (
        <button
          onClick={onPrev}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 hidden md:block"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}

      {totalImages > 1 && onNext && (
        <button
          onClick={onNext}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 hidden md:block"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {/* Image counter */}
      {totalImages > 1 && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded-full">
          {currentIndex + 1} de {totalImages}
        </div>
      )}

      {/* Image with swipe support */}
      <div 
        ref={swipeRef}
        className="max-w-full max-h-full flex items-center justify-center cursor-grab active:cursor-grabbing select-none"
      >
        <img
          src={imageUrl}
          alt={title}
          className="max-w-full max-h-full object-contain pointer-events-none"
          style={{ maxHeight: '90vh', maxWidth: '90vw' }}
          draggable={false}
        />
      </div>

      {/* Image title */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-center">
        <p className="text-sm bg-black bg-opacity-50 px-4 py-2 rounded-lg">
          {title}
        </p>
      </div>
      
      {/* Mobile swipe instruction on first load */}
      {totalImages > 1 && (
        <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 text-white text-center md:hidden">
          <p className="text-xs bg-black bg-opacity-50 px-3 py-1 rounded-lg">
            ← Arraste para navegar →
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageModal;